import { useEffect, useState } from 'react';
import { reresolvePreset, type CabinetInstance, type ParametricPresetContent, type PresetContent } from '@scs/domain-engine';
import type { PresetSummaryDto } from '@scs/schemas';
import { IntInput, MmInput } from '../lib/ui';
import type { EditorStore } from './store';
import { loadVersion } from './presets';

type Exec = EditorStore['exec'];

function useVersion(versionId: string | null) {
  const [content, setContent] = useState<PresetContent | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!versionId) return;
    let cancel = false;
    setContent(null);
    loadVersion(versionId)
      .then((c) => !cancel && setContent(c))
      .catch((e) => !cancel && setError((e as Error).message));
    return () => {
      cancel = true;
    };
  }, [versionId]);
  return { content, error };
}

/** Preset parameters of a cabinet instance, version update with diff, and detach. */
export function PresetSection({ inst, exec, disabled, presets }: { inst: CabinetInstance; exec: Exec; disabled: boolean; presets: PresetSummaryDto[] | null }) {
  const link = inst.preset!;
  const { content, error } = useVersion(link.versionId);
  const summary = presets?.find((p) => p.id === link.presetId);
  const [update, setUpdate] = useState<null | { content: ParametricPresetContent; versionId: string; version: number }>(null);
  const [paramError, setParamError] = useState<string | null>(null);

  if (error) return <div className="prop-group"><p className="error-text">Preset nije dostupan: {error}</p></div>;
  if (!content) return <div className="prop-group faint">Učitavanje preseta…</div>;
  if (content.kind !== 'parametric') return null;

  const setValue = (key: string, v: number) => {
    const values = { ...link.values, [key]: v };
    const r = reresolvePreset(content, link.values, content, values, inst.params);
    if (!r.params) {
      setParamError(r.errors.join(' '));
      return;
    }
    setParamError(null);
    exec(`Preset parametar ${key}`, (d) => {
      const c = d.cabinets.find((x) => x.id === inst.id)!;
      c.params = r.params!;
      c.preset = { ...link, values: r.values };
    });
  };

  const newer = summary && summary.latestVersion > link.version;

  return (
    <div className="prop-group">
      <h3>Preset</h3>
      <div className="prop-row">
        <label>Izvor</label>
        <span title={summary?.description}>
          {summary?.name ?? 'Preset'} <span className="faint">v{link.version}</span>
        </span>
      </div>
      {content.params.map((p) => (
        <div className="prop-row" key={p.key}>
          <label title={`${p.dim === 'length' ? `${p.min / 1000}–${p.max / 1000} mm` : `${p.min}–${p.max}`}`}>{p.label}</label>
          {p.dim === 'length' ? (
            <MmInput label={p.label} value={link.values[p.key] ?? p.default} min={p.min / 1000} max={p.max / 1000} step={p.step / 1000} disabled={disabled} onCommit={(v) => setValue(p.key, v)} />
          ) : (
            <IntInput label={p.label} value={link.values[p.key] ?? p.default} min={p.min} max={p.max} disabled={disabled} onCommit={(v) => setValue(p.key, v)} />
          )}
        </div>
      ))}
      {paramError && <p className="error-text">{paramError}</p>}
      {content.notes.map((n, i) => (
        <p key={i} className="faint" style={{ fontSize: 11, margin: '4px 0' }}>
          {n}
        </p>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        {newer && (
          <button
            className="btn sm"
            disabled={disabled}
            onClick={async () => {
              const c = await loadVersion(summary!.latestVersionId);
              if (c.kind === 'parametric') setUpdate({ content: c, versionId: summary!.latestVersionId, version: summary!.latestVersion });
            }}
          >
            Ažuriraj na v{summary!.latestVersion}…
          </button>
        )}
        <button
          className="btn sm ghost"
          disabled={disabled}
          title="Element postaje samostalan; veza sa presetom se uklanja (može se poništiti)."
          onClick={() =>
            exec('Odvoji od preseta', (d) => {
              d.cabinets.find((x) => x.id === inst.id)!.preset = null;
            })
          }
        >
          Odvoji od preseta
        </button>
      </div>
      {update && (
        <UpdateDialog
          inst={inst}
          oldContent={content}
          next={update}
          onClose={() => setUpdate(null)}
          onApply={(params, values) => {
            exec(`Ažuriranje preseta na v${update.version}`, (d) => {
              const c = d.cabinets.find((x) => x.id === inst.id)!;
              c.params = params;
              c.preset = { presetId: link.presetId, versionId: update.versionId, version: update.version, values };
            });
            setUpdate(null);
          }}
        />
      )}
    </div>
  );
}

const COUNT_PATHS = new Set(['dividers', 'shelves.count']);

/** Diff values are JSON of µm leaves; show lengths in mm. */
function fmtLeaf(path: string, json: string): string {
  try {
    const v = JSON.parse(json) as unknown;
    if (typeof v === 'number') return COUNT_PATHS.has(path) ? String(v) : `${v / 1000} mm`;
    return typeof v === 'string' ? v : json;
  } catch {
    return json;
  }
}

function UpdateDialog({
  inst,
  oldContent,
  next,
  onClose,
  onApply,
}: {
  inst: CabinetInstance;
  oldContent: ParametricPresetContent;
  next: { content: ParametricPresetContent; version: number };
  onClose(): void;
  onApply(params: CabinetInstance['params'], values: Record<string, number>): void;
}) {
  const plan = reresolvePreset(oldContent, inst.preset!.values, next.content, inst.preset!.values, inst.params);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="card modal wide" role="dialog" aria-modal onClick={(e) => e.stopPropagation()}>
        <h2>
          Ažuriranje {inst.name} na verziju {next.version}
        </h2>
        {plan.errors.length > 0 && (
          <p className="error-text">
            Nova verzija nije primjenjiva na ovaj element: {plan.errors.join(' ')}
          </p>
        )}
        {plan.overrides.length > 0 && <p className="muted">Lokalne izmjene koje se zadržavaju: {plan.overrides.join(', ')}</p>}
        {plan.dropped.length > 0 && <p className="error-text">Parametri koji više ne postoje u novoj verziji (vrijednost se gubi): {plan.dropped.join(', ')}</p>}
        <table className="grid diff-table">
          <thead>
            <tr>
              <th>Svojstvo</th>
              <th>Sada</th>
              <th>Nakon ažuriranja</th>
            </tr>
          </thead>
          <tbody>
            {plan.diff.length === 0 && (
              <tr>
                <td colSpan={3} className="faint">
                  Nema promjena mjera za ovaj element.
                </td>
              </tr>
            )}
            {plan.diff.map((d) => (
              <tr key={d.path}>
                <td>{d.label}</td>
                <td>{fmtLeaf(d.path, d.before)}</td>
                <td>{fmtLeaf(d.path, d.after)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="faint" style={{ fontSize: 11 }}>
          Ažuriranje je jedna komanda — Poništi (Ctrl+Z) vraća prethodnu verziju.
        </p>
        <div className="actions">
          <button className="btn" onClick={onClose}>
            Otkaži
          </button>
          <button className="btn primary" disabled={!plan.params} onClick={() => plan.params && onApply(plan.params, plan.values)}>
            Primijeni
          </button>
        </div>
      </div>
    </div>
  );
}
